// @ts-strict-ignore
import partition from 'lodash/partition';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import moment from 'moment';

import {
  CohortGroupEntry,
  FiltersOperator,
  isFormulaEntry,
  isMetricEntry,
  isSavedMetricEntry,
  MetricEntry,
  QueryBehaviorEntry,
  QueryMetricEntry,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {createDefaultCohortMetricEntry} from 'common/report/util/cohorts/transformations/entries';
import {getInlineCustomPropertyId} from 'common/widgets/custom-property-modal/util';
import {ListOperator, NumberOperator, StringOperator} from 'common/widgets/property-filter-menu/models/types';
import {ALL_MATH_TYPES, PropertyType, ResourceType} from 'common/report/constants';
import {DateRangeType, PRIMITIVE_MATH_TYPES} from 'common/types/reports/bookmark';
import {CustomPropertyResourceType} from 'common/report/stores/custom-properties';
import {
  Behavior,
  BehavioralAttributes,
  CustomProperty,
  Segmentation,
  SegmentationPropertyFields,
} from 'common/types/reports/query';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {SUPPORTED_BEHAVIORAL_AGGREGATIONS} from 'common/report/util/cohorts/utils';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {
  RECURRING_REVENUE_CHANGE,
  RECURRING_REVENUE_CHANGE_TYPE,
  TOP_PEOPLE,
} from 'common/query-builder/query-block/properties-menu/constants';
import {isFirstTimeEventFilter} from 'common/widgets/property-filter-menu/models/utils';
import {InlineCohortSegment} from 'common/query-builder/query-block/segment-menu/types';
import {isScdPropertyName} from 'common/report/util/properties';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {isProperty} from 'common/query-builder/query-block/properties-menu/types';

import {FilterClause} from '../models/filter-clause';

type FiltersByResourceType = {
  [type in ResourceType]: PropertyFilter[];
};

function hasUniformResourceTypes(filters: PropertyFilter[]) {
  return filters?.length <= 1 || new Set(filters.map((filter) => filter.resourceType)).size === 1;
}

function groupPropertyFiltersByResourceType(filters: PropertyFilter[]): FiltersByResourceType {
  const groups = {
    [ResourceType.Event]: [],
    [ResourceType.Formula]: [],
    [ResourceType.User]: [],
    [ResourceType.Cohort]: [],
    [ResourceType.Metric]: [],
    [ResourceType.Other]: [],
  };

  for (const filter of filters) {
    groups[filter.resourceType].push(filter);
  }

  return groups;
}

function getBehavioralFilterFromQueryBehaviorEntry(
  entry: QueryBehaviorEntry,
  label = ``,
  frequency = new PropertyFilter({
    propertyType: PropertyType.Number,
    filterOperator: NumberOperator.AtLeast,
    filterValue: 1,
    resourceType: ResourceType.User,
  }),
): PropertyFilter {
  const customProperty: CustomProperty = {
    name: label,
    behavior: pick(entry, BehavioralAttributes) as Behavior,
    description: ``,
    referencedBy: [],
    referencedDirectlyBy: [],
    referencedRawEventProperties: [],
    propertyType: frequency.propertyType,
    resourceType: CustomPropertyResourceType.People,
  };

  const customPropertyId = getInlineCustomPropertyId(null);

  return new PropertyFilter({
    ...frequency.getAttributes(),
    customProperty,
    customPropertyId,
    resourceType: ResourceType.User,
  });
}

function hasMultiAttributionBehavior(filter) {
  return (
    filter.behavior?.aggregationOperator === ALL_MATH_TYPES.MULTI_ATTRIBUTION ||
    filter.customProperty?.behavior?.aggregationOperator === ALL_MATH_TYPES.MULTI_ATTRIBUTION
  );
}

function getCohortEntriesFromBehavior(
  behavior: QueryBehaviorEntry,
  {
    label = ``,
    filters = [],
    filtersOperator = FiltersOperator.And,
    entryFiltersOperator = FiltersOperator.And,
    behavioralFiltersOperator = FiltersOperator.And,
    groupingOperator = FiltersOperator.And,
    frequency,
    frequencyExclusive,
  }: {
    label?: string;
    filters?: PropertyFilter[];
    filtersOperator?: FiltersOperator;
    entryFiltersOperator?: FiltersOperator;
    behavioralFiltersOperator?: FiltersOperator;
    groupingOperator?: FiltersOperator.And | FiltersOperator.Or;
    frequency?: PropertyFilter;
    frequencyExclusive?: boolean;
  } = {},
): CohortGroupEntry[] {
  const {event: eventFilters, user: userFilters, cohort: cohortFilters} = groupPropertyFiltersByResourceType(filters);

  let convertedForcedInlineFilters = null;

  if (userFilters.some((filter) => filter.behavior)) {
    convertedForcedInlineFilters = userFilters.map((filter) =>
      filter.behavior &&
      ![RECURRING_REVENUE_CHANGE.value, RECURRING_REVENUE_CHANGE_TYPE.value].includes(filter.behavior.behaviorType)
        ? getBehavioralFilterFromQueryBehaviorEntry(
            {
              ...filter.behavior,
              // Attribution behaviors need to keep their relative dateRange lookback
              ...(!hasMultiAttributionBehavior(filter) && {dateRange: behavior.dateRange}),
            } as QueryBehaviorEntry,
            label,
            new PropertyFilter({
              propertyType: filter.propertyType,
              filterOperator: filter.filterOperator,
              filterValue: filter.filterValue,
              resourceType: filter.resourceType,
            }),
          )
        : filter,
    );
  }
  const inlineUserFilters = convertedForcedInlineFilters ?? userFilters;

  const [attributionEventFilters, otherEventFilters] = partition(eventFilters, hasMultiAttributionBehavior);
  const [attributionUserFilters, otherUserFilters] = partition(inlineUserFilters, hasMultiAttributionBehavior);

  const behaviorInlineFilters = [
    ...otherEventFilters,
    ...otherUserFilters,
    // behavior.filters is the 'nth time' filter aka 'First time ever' filter
    // It must come after filters that apply to the 'First time ever' lookup
    ...behavior.filters,
    // Attribution filters originate in the breakdown and are applied after the 'First time ever' filter
    ...attributionEventFilters,
    ...attributionUserFilters,
  ];

  let behaviorFilters = [
    getBehavioralFilterFromQueryBehaviorEntry(
      {
        ...behavior,
        filtersOperator,
        filters: behaviorInlineFilters,
      },
      label,
      frequency,
    ),
  ];

  // Apply rational frequencies as two behavioral filters if frequency is exclusive (e.g. distribution on Insights)
  if (frequencyExclusive && Array.isArray(frequency.filterValue) && frequency.filterValue?.length > 1) {
    behaviorFilters = [
      getBehavioralFilterFromQueryBehaviorEntry(
        {
          ...behavior,
          filtersOperator,
          filters: behaviorInlineFilters,
        },
        label,
        new PropertyFilter({
          ...frequency.getAttributes(),
          filterOperator: NumberOperator.AtLeast,
          filterValue: frequency.filterValue[0],
        }),
      ),
      getBehavioralFilterFromQueryBehaviorEntry(
        {
          ...behavior,
          filtersOperator,
          filters: behaviorInlineFilters,
        },
        label,
        new PropertyFilter({
          ...frequency.getAttributes(),
          filterOperator: NumberOperator.LessThan,
          filterValue: frequency.filterValue[1],
        }),
      ),
    ];
  }

  const entry = {
    ...createDefaultCohortMetricEntry(),
    filters: [...behaviorFilters],
    filtersOperator: entryFiltersOperator,
    behavioralFiltersOperator,
    groupingOperator,
  };

  return [
    entry,
    ...cohortFilters.map((filter) => ({
      ...createDefaultCohortMetricEntry(),
      filters: [filter],
      filtersOperator: FiltersOperator.And,
    })),
  ];
}

export function createNestedCohort(cohorts: Cohort[]) {
  const cohort = new Cohort();
  // Prevents the create-entity-modal from complaining about missing createdBy
  cohort.createdBy = {
    name: null,
    email: null,
    id: null,
  };
  cohort.dataGroupId = cohorts[0].dataGroupId;

  /** if defining a single cohort, we expose the full definition
   * unlike multiple cohorts where we OR these are cohort filters */
  if (cohorts.length === 1) {
    return cohorts[0];
  }

  const createCohortFilter = (coh: Cohort) =>
    new PropertyFilter({
      resourceType: ResourceType.Cohort,
      propertyType: PropertyType.List,
      propertyDefaultType: PropertyType.List,
      filterOperator: ListOperator.Contains,
      filterValue: [
        {
          cohort: {
            raw_cohort: coh.serialize(),
            negated: false,
            name: coh.name,
          },
        } as InlineCohortSegment,
      ],
    });

  cohort.groups = cohorts.map((coh) => ({
    ...createDefaultCohortMetricEntry(),
    filters: [createCohortFilter(coh)],
    groupingOperator: FiltersOperator.Or,
  }));

  return cohort;
}

export function translateMetricToCohort(
  entry: QueryMetricEntry | MetricEntry,
  {
    name,
    dateRange,
    keepUserAggregation,
    filters: segmentFilters,
    frequency,
    frequencyExclusive = false,
  }: {
    name: string; // name of the clause
    dateRange: DateRange;
    keepUserAggregation: boolean;
    filters: PropertyFilter[]; // Segment filters are anded together
    // Indicates whether user aggregation should be simplified to is set
    frequency?: PropertyFilter;
    frequencyExclusive?: boolean;
  },
): Cohort {
  frequencyExclusive ??= false;
  const cohort = new Cohort();
  // TODO: Make translateMetricToCohort work for metric entry
  if (isMetricEntry(entry) || isFormulaEntry(entry) || isSavedMetricEntry(entry)) {
    return cohort;
  }

  let {
    event,
    aggregationOperatorPerUser,
    property,
    filters: entryFilters,
    filtersOperator = FiltersOperator.And,
    dataGroupId,
  } = entry;

  const {label} = event;
  let additionalFilters = segmentFilters;
  const [firstTimeFilters, inlineFilters] = partition(
    entryFilters.map((filter) =>
      mapForScdAndRevenue(FilterClause.fromPropertyFilter(filter), {dateRange}).toPropertyFilter(),
    ),
    isFirstTimeEventFilter,
  );
  const hasFirstTimeFilter = firstTimeFilters.length > 0;

  // prevents a case where the entry has `or` filters with only one entry
  filtersOperator =
    filtersOperator === FiltersOperator.Or && inlineFilters.length <= 1 ? FiltersOperator.And : filtersOperator;

  // Prevents the create-entity-modal from complaining about missing createdBy
  cohort.createdBy = {
    name: null,
    email: null,
    id: null,
  };
  cohort.dataGroupId = dataGroupId;
  cohort.name = name;
  cohort.unsavedId = name;

  let behavior: QueryBehaviorEntry = {
    type: SelectorTypeDefinitions.Behavior,
    dateRange,
    event,
    filters: hasFirstTimeFilter ? firstTimeFilters : [],
    filtersOperator,
    aggregationOperator: PRIMITIVE_MATH_TYPES.TOTAL,
    property: null,
    aggregationOperatorPerUser: null,
  };

  /** handles total per user entries  */
  if (property && SUPPORTED_BEHAVIORAL_AGGREGATIONS.includes(aggregationOperatorPerUser)) {
    /** for entries with anything other that distribution, we translate to "did event at least once" */
    if (!keepUserAggregation) {
      additionalFilters = [
        ...additionalFilters,
        new PropertyFilter({
          propertyType: PropertyType.String,
          propertyDefaultType: PropertyType.String,
          filterOperator: StringOperator.Set,
          propertyName: property.value,
          resourceType: property.resourceType,
        }),
      ];
    } else {
      behavior = {
        ...behavior,
        aggregationOperator: aggregationOperatorPerUser,
        property,
      };
    }
  }

  additionalFilters = additionalFilters.map((filter) => {
    if (filter.behavior?.behaviorType === `$behavior_aggregation`) {
      filter.behavior.dateRange = dateRange;
      filter.behavior.filters[0].behavior.dateRange = dateRange;
    }
    return filter;
  });

  if (
    property &&
    property.value === RECURRING_REVENUE_CHANGE.value &&
    isProperty(property) &&
    property.behavior.changeType !== `any`
  ) {
    additionalFilters = [
      ...additionalFilters,
      mapForScdAndRevenue(
        FilterClause.fromPropertyFilter(
          new PropertyFilter({
            behavior: {
              aggregationOperator: null,
              filters: [],
              filtersOperator: FiltersOperator.And,
              event: null,
              behaviorType: RECURRING_REVENUE_CHANGE_TYPE.value,
              offset: property.behavior.offset,
              property: property.behavior.property,
            },
            propertyType: PropertyType.String,
            propertyDefaultType: PropertyType.String,
            filterOperator: StringOperator.Equals,
            propertyName: RECURRING_REVENUE_CHANGE_TYPE.value,
            filterValue: [property.behavior.changeType],
            resourceType: property.resourceType,
          }),
        ),
        {dateRange},
      ).toPropertyFilter(),
    ];
  }
  if (hasFirstTimeFilter && additionalFilters.length) {
    /** events with 1st time filters and global filters which apply after the fact of event occurrence, we define those as first value of */
    additionalFilters = additionalFilters.map((filter) => {
      if (filter.resourceType !== ResourceType.Event) {
        return filter;
      }

      // instead of specifying a filter like `Country = United States`, we convert these to
      // did Event Name with First Value of Country = United States, a behavioral filter
      const behaviorEntry = {
        ...behavior,
        aggregationOperator: PRIMITIVE_MATH_TYPES.FIRST_VALUE,
        property: {
          label: null,
          resourceType: ResourceType.Event,
          propertyDefaultType: filter.propertyDefaultType ?? filter.propertyType,
          type: filter.propertyType,
          value: filter.propertyName,
          ...pickBy(
            filter,
            (value, key) =>
              SegmentationPropertyFields.has(key as keyof Segmentation) && key !== `propertyName` && Boolean(value),
          ),
        },
        filtersOperator,
        filters: inlineFilters.filter((f) => f.resourceType === ResourceType.Event),
      };

      const isDataGroupMapping = filter.dataGroupId && Array.isArray(filter.propertyName);
      if (isDataGroupMapping) {
        // If the filter is on a joined data group property, the mapping has already
        // been applied to the first time behavior above.
        delete filter.dataGroupId;
        delete filter.propertyName;
      }

      return getBehavioralFilterFromQueryBehaviorEntry(behaviorEntry, label.toString(), filter);
    });
  }

  /** profile mode without aggregations translates directly to a cohort definition with filters */
  if (event.value === TOP_PEOPLE.value) {
    const groups = [];
    if (filtersOperator === FiltersOperator.Or) {
      for (const inlineFilter of inlineFilters) {
        groups.push({
          ...createDefaultCohortMetricEntry(),
          filters: [inlineFilter, ...additionalFilters],
          filtersOperator: FiltersOperator.And,
          groupingOperator: FiltersOperator.Or,
        });
      }
    } else {
      groups.push({
        ...createDefaultCohortMetricEntry(),
        filters: [...inlineFilters, ...additionalFilters],
        filtersOperator,
      });
    }

    cohort.groups = groups;
    // Apply event filters for common condition
    /** @todo: [pablo] Use the same hasUniformResourceTypes approach for query simplification */
  } else if (
    filtersOperator === FiltersOperator.And ||
    ((additionalFilters?.length === 0 || hasFirstTimeFilter) && hasUniformResourceTypes(inlineFilters))
  ) {
    cohort.groups = getCohortEntriesFromBehavior(behavior, {
      label: label.toString(),
      filters: [...inlineFilters, ...additionalFilters],
      filtersOperator,
      entryFiltersOperator: filtersOperator,
      frequency,
      frequencyExclusive,
    });
  } else if (filtersOperator === FiltersOperator.Or) {
    // Create n entries for or-ed filters with a and-ed segment filter

    /**
     * @TODO pablo - this has an inherent bug for frequency analysis where splitting logical ORs into ANDs
     * is not performing the same behavior count, it's not the same to count Event A and (B or C) and to
     * count (Event A and B) or (Event A and C), they are logically equivalent but the counts do not reflect
     * the intended behavior
     */
    cohort.groups = inlineFilters.reduce(
      (entries, filter) => [
        ...entries.map((etr, i) => {
          return i === entries.length - 1
            ? {
                ...etr,
                groupingOperator: FiltersOperator.Or,
              }
            : etr;
        }),
        ...getCohortEntriesFromBehavior(behavior, {
          label: label.toString(),
          filters: [filter, ...additionalFilters],
          filtersOperator: FiltersOperator.And,
          groupingOperator: FiltersOperator.And,
          frequency,
          frequencyExclusive,
        }),
      ],
      [],
    );
  } else {
    throw new Error(`Invalid filtersOperator ${filtersOperator}, only and/or are supported for translation`);
  }

  return cohort;
}

export function mapForScdAndRevenue(
  filter: FilterClause,
  {
    dateRange,
  }: {
    dateRange: DateRange;
  },
) {
  // -1 here from is start of interval, to is end of interval. so there's 1 day diff
  // min interval for offset is day.
  const offsetDays =
    // date range here is not possible to be relative range, so from & to can can always be passed in to moment
    // this typecheck here is just extra defensive
    typeof dateRange.from === `string` && typeof dateRange.to === `string`
      ? moment(dateRange.from).diff(moment(dateRange.to), `days`) - 1
      : 0;

  if ([RECURRING_REVENUE_CHANGE.value, RECURRING_REVENUE_CHANGE_TYPE.value].includes(filter.value)) {
    return filter.extend({
      behavior: {
        ...filter.behavior,
        offset: {
          unit: filter.behavior.offset.value === `$start` ? TimeUnit.Day : filter.behavior.offset.unit,
          value: filter.behavior.offset.value === `$start` ? offsetDays : filter.behavior.offset.value,
        },
        property: {
          ...filter.behavior.property,
          scdOptions: {
            type: `latest`,
            dateRange: {
              type: DateRangeType.On,
              from: dateRange.to,
              to: dateRange.to,
            },
          },
        },
      },
    });
  }
  if (isScdPropertyName(filter.value)) {
    const scdOptionsType = filter.scdOptions?.type ?? `latest`;
    let scdOptionsDateRange;
    if (scdOptionsType === `latest`) {
      const scdOnDate = dateRange?.to;
      scdOptionsDateRange = {
        type: DateRangeType.On,
        from: scdOnDate,
        to: scdOnDate,
      };
    } else {
      scdOptionsDateRange = {
        type: DateRangeType.Between,
        from: dateRange?.from ?? filter.scdOptions?.dateRange?.from,
        to: dateRange?.to ?? filter.scdOptions?.dateRange?.to,
      };
    }

    return filter.extend({
      scdOptions: {
        type: scdOptionsType,
        dateRange: scdOptionsDateRange,
      },
    });
  }
  return filter;
}
